import { useEffect, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Loader2,
  RefreshCw,
  Search,
  ShieldAlert,
  TriangleAlert,
  Wrench,
} from 'lucide-react';
import { fetchAiToolCatalog, type AiToolCatalog, type AiToolInfo } from '../../lib/api';

function riskClass(risk?: string | null) {
  switch (risk) {
    case 'critical':
      return 'bg-zt-danger/15 text-zt-danger';
    case 'high':
      return 'bg-amber-500/15 text-amber-400';
    default:
      return 'bg-zt-warning/15 text-zt-warning';
  }
}

function ToolRow({ tool }: { tool: AiToolInfo }) {
  const [open, setOpen] = useState(false);
  const params = (tool.parameters?.properties ?? {}) as Record<string, { description?: string }>;
  const paramNames = Object.keys(params);

  return (
    <li className="rounded-lg border border-zt-border bg-zt-bg">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-2 px-2.5 py-2 text-left"
      >
        {open ? (
          <ChevronDown size={13} className="mt-0.5 shrink-0 text-zt-muted" />
        ) : (
          <ChevronRight size={13} className="mt-0.5 shrink-0 text-zt-muted" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span
              className={`truncate font-mono text-xs ${
                tool.allowed ? 'text-slate-200' : 'text-zt-muted line-through'
              }`}
            >
              {tool.name}
            </span>
            {tool.approvalRequired && (
              <span
                className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-medium uppercase ${riskClass(
                  tool.riskLevel
                )}`}
                title="Requires human approval before it runs"
              >
                {tool.riskLevel ?? 'gated'}
              </span>
            )}
          </div>
          {!open && (
            <p className="mt-0.5 line-clamp-1 text-[11px] text-zt-muted">{tool.description}</p>
          )}
        </div>
      </button>
      {open && (
        <div className="space-y-2 border-t border-zt-border px-2.5 py-2">
          <p className="text-[11px] leading-relaxed text-slate-300">{tool.description}</p>
          {paramNames.length > 0 && (
            <div>
              <p className="mb-1 text-[10px] uppercase tracking-wide text-zt-muted">Parameters</p>
              <ul className="space-y-0.5">
                {paramNames.map((p) => (
                  <li key={p} className="text-[11px] text-zt-muted">
                    <span className="font-mono text-slate-300">{p}</span>
                    {params[p]?.description ? ` — ${params[p].description}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {!tool.allowed && (
            <p className="text-[11px] text-zt-warning">
              Blocked by the AI_TOOL_ALLOWLIST configuration.
            </p>
          )}
        </div>
      )}
    </li>
  );
}

export default function ToolCatalogPanel() {
  const [catalog, setCatalog] = useState<AiToolCatalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');

  const load = async (refresh = false) => {
    setLoading(true);
    const res = await fetchAiToolCatalog(refresh);
    setCatalog(res.ok && res.data ? res.data : null);
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const filtered = useMemo(() => {
    const tools = catalog?.tools ?? [];
    const needle = query.trim().toLowerCase();
    return tools.filter(
      (t) =>
        (!category || t.category === category) &&
        (!needle ||
          t.name.toLowerCase().includes(needle) ||
          t.description.toLowerCase().includes(needle))
    );
  }, [catalog, category, query]);

  const grouped = useMemo(() => {
    const map = new Map<string, AiToolInfo[]>();
    for (const tool of filtered) {
      const list = map.get(tool.category) ?? [];
      list.push(tool);
      map.set(tool.category, list);
    }
    return [...map.entries()];
  }, [filtered]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="space-y-2 border-b border-zt-border p-3">
        <div className="flex items-center gap-2">
          <Wrench size={14} className="text-zt-primary" />
          <span className="text-sm font-medium text-white">MCP tools</span>
          <span className="text-xs text-zt-muted">
            {catalog?.connected ? `${catalog.total} available` : 'disconnected'}
          </span>
          <button
            type="button"
            onClick={() => void load(true)}
            className="ml-auto rounded p-1 text-zt-muted hover:text-zt-primary"
            aria-label="Refresh tool catalog"
            title="Refresh"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zt-muted" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search tools"
            className="w-full rounded-lg border border-zt-border bg-zt-bg py-1.5 pl-8 pr-2 text-xs text-slate-200 placeholder-zt-muted focus:border-zt-primary focus:outline-none"
          />
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="w-full rounded-lg border border-zt-border bg-zt-bg px-2 py-1.5 text-xs text-slate-200 focus:border-zt-primary focus:outline-none"
        >
          <option value="">All categories</option>
          {(catalog?.categories ?? []).map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        {catalog?.allowlistActive && (
          <p className="flex items-center gap-1 text-[11px] text-zt-warning">
            <ShieldAlert size={11} /> An allowlist is restricting the tool surface.
          </p>
        )}
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
        {loading && !catalog && (
          <div className="flex items-center gap-2 text-sm text-zt-muted">
            <Loader2 size={14} className="animate-spin" /> Loading tools…
          </div>
        )}
        {!loading && catalog && !catalog.connected && (
          <div className="flex items-start gap-2 rounded-lg border border-zt-danger/30 bg-zt-danger/5 p-3 text-xs text-zt-danger">
            <TriangleAlert size={14} className="mt-0.5 shrink-0" />
            <span>{catalog.error ?? 'The MCP server is not reachable.'}</span>
          </div>
        )}
        {grouped.map(([cat, tools]) => (
          <div key={cat}>
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-zt-muted">
              {cat} · {tools.length}
            </p>
            <ul className="space-y-1">
              {tools.map((t) => (
                <ToolRow key={t.name} tool={t} />
              ))}
            </ul>
          </div>
        ))}
        {!loading && catalog?.connected && grouped.length === 0 && (
          <p className="text-sm text-zt-muted">No tools match that filter.</p>
        )}
      </div>
    </div>
  );
}
